import { useCallback, useState, useEffect, useMemo } from "react";
import {
  Grid,
  Button,
  Text,
  Flex,
  Box,
  ScrollArea,
  Skeleton,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { memo } from "react";
import { BiSearch } from "react-icons/bi";
import { getTimeInAMPMFormat } from "src/utils/dateHelper";
import { useModals } from "@mantine/modals";
import SelectMember from "../SelectMember";
import { ModalId } from "src/utils/constants";
import {
  CheckInManyQuery,
  useCheckInManyQuery,
  useGetnewCheckInSubscription,
} from "src/generated/graphql";
import { useAppContext } from "src/api/AppContext";
import ExpandView from "./ExpandView";
import { getStatusObj } from "..";
import TextInput from "src/components/TextInput/TextInput";

export type ICheckInManyQuery = CheckInManyQuery["checkInMany"];

function RightSide() {
  const modals = useModals();
  const theme = useMantineTheme();
  const [search, setSearch] = useDebouncedState("", 400);
  const { currentLocation, selectedCheckInId, setselectedCheckInId } =
    useAppContext();
  const [allCheckIns, setallCheckIns] = useState<ICheckInManyQuery>([]);
  const [filterCheckIns, setfilterCheckIns] = useState<ICheckInManyQuery>([]);

  const { data: newCheckIns } = useGetnewCheckInSubscription({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation?.id,
  });

  useEffect(() => {
    // MERGE New Data on start (newCheckIn subscription)
    if (newCheckIns?.createCheckInSubscription?.newCheckIn) {
      setallCheckIns([
        newCheckIns.createCheckInSubscription.newCheckIn,
        ...allCheckIns,
      ]);
    }
  }, [newCheckIns]);

  const { loading } = useCheckInManyQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      locationId: currentLocation?.id,
      todayRecords: true,
    },
    skip: !currentLocation?.id,
    onCompleted(data) {
      setallCheckIns(data.checkInMany || []);
    },
  });

  const lastHourCount = useMemo(() => {
    const currentTime = new Date();
    const oneHourAgo = new Date(currentTime.getTime() - 60 * 60 * 1000);
    return (
      allCheckIns.filter((item) => new Date(item.createdDate) >= oneHourAgo)
        .length || 0
    );
  }, [allCheckIns]);

  useEffect(() => {
    if (allCheckIns.length) {
      const filteredData = allCheckIns.filter((item) => {
        const user = item.client.user;
        return (
          user.firstName.toLowerCase().includes(search.toLowerCase()) ||
          user.lastName.toLowerCase().includes(search.toLowerCase())
        );
      });
      setfilterCheckIns(filteredData);
    } else {
      setfilterCheckIns(allCheckIns || []);
    }
  }, [allCheckIns, search]);

  const openModalSelect = useCallback(() => {
    modals.openModal({
      modalId: ModalId.CheckInSelectMember,
      closeOnClickOutside: false,
      title: "Select a Member to Check In",
      children: <SelectMember />,
    });
  }, []);

  return (
    <Grid.Col
      span="auto"
      sx={(theme) => ({
        background: theme.colors.secondary[6],
        padding: "0",
        borderLeft: `1px solid ${theme.colors.secondary[0]}`,
      })}
    >
      <Flex
        direction={"column"}
        justify={"space-between"}
        sx={{ height: "100%" }}
      >
        <Box>
          <Text
            fw={500}
            size={22}
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.colors.secondary[0]}`,
              borderTop: `1px solid ${theme.colors.secondary[0]}`,
              color: theme.colors.secondaryTextColor[6],
            })}
            p={"8px"}
            mb={5}
          >
            Check-In Queue
          </Text>

          <TextInput
            m={"8px"}
            radius={"xl"}
            size="sm"
            icon={<BiSearch />}
            placeholder="Search"
            defaultValue={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />

          <ScrollArea style={{ height: "64vh" }} type="auto">
            <>
              <ExpandView detail={filterCheckIns[0]} />

              {!filterCheckIns.length && (
                <Box mb={"sm"} p={7}>
                  {loading ? (
                    <Skeleton w="100%" height={60} mb={10} />
                  ) : (
                    <Text align="center" fw={600} size={18}>
                      No Check In Found
                    </Text>
                  )}
                </Box>
              )}

              {filterCheckIns?.map((c) => {
                const statusObj = getStatusObj(
                  c.client?.memberships || [],
                  currentLocation?.id,
                  theme,
                  c.isAccepted
                );

                return (
                  <Flex
                    key={c.id}
                    p={"xs"}
                    justify={"space-between"}
                    sx={(theme) => ({
                      background:
                        selectedCheckInId === c.id
                          ? theme.colors.primary[6]
                          : statusObj.color || "transparent",
                      cursor: "pointer",
                    })}
                    onClick={() => setselectedCheckInId(c.id)}
                  >
                    <Flex>
                      <Box
                        sx={(theme) => ({
                          height: 50,
                          width: 50,
                          background: theme.colors.gray[2],
                          borderRadius: 4,
                        })}
                      >
                        {c.client.user?.profilePicUrl && (
                          <img
                            src={c.client.user.profilePicUrl}
                            style={{
                              height: "100%",
                              width: "100%",
                              objectFit: "cover",
                              borderRadius: 4,
                              maxHeight: 50,
                              maxWidth: 50,
                            }}
                            alt="profile pic"
                          />
                        )}
                      </Box>
                      <Box pl={"xs"}>
                        <Text size={14}>
                          {c.client.user.firstName} {c.client.user.lastName}
                        </Text>
                        <Text size={14} fw={500}>
                          {c.client.memberStatus}
                          {c.client.leadStatus && `- ${c.client.leadStatus}`}
                        </Text>
                      </Box>
                    </Flex>
                    <Text size={14} pr={5}>
                      {getTimeInAMPMFormat(c.createdDate)}
                    </Text>
                  </Flex>
                );
              })}
            </>
          </ScrollArea>
        </Box>

        <Box
          mt={"xs"}
          p={"0 0px"}
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.colors.secondary[0]}`,
          })}
        >
          <Box
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.colors.secondary[0]}`,
              borderTop: `1px solid ${theme.colors.secondary[0]}`,
            })}
            mb={8}
            p={"0 10px"}
          >
            <Flex justify={"space-between"}>
              <Text fw={600}>Last Hour:</Text>
              <Text fw={600}>{lastHourCount}</Text>
            </Flex>
            <Flex justify={"space-between"}>
              <Text fw={600}>Today:</Text>
              <Text fw={600}>{allCheckIns.length}</Text>
            </Flex>
          </Box>
          <Box p={"0 10px"}>
            <Button
              fullWidth
              mb={"xs"}
              radius={"xl"}
              size="sm"
              onClick={openModalSelect}
            >
              Check In
            </Button>
          </Box>
        </Box>
      </Flex>
    </Grid.Col>
  );
}

export default memo(RightSide);
